import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import Modal from './Modal';
import { accountingAPI } from '../../utils/api';
import { today, fmt, money, apiError } from '../../utils/helpers';

/**
 * Receive / pay money against something (sale, purchase, or free transaction).
 * Props: title, suggestedUsd (amount still open, in USD), onSubmit(payload) -> Promise, onClose, children (extra fields)
 */
export default function MoneyModal({ title, suggestedUsd, onSubmit, onClose, extra, submitLabel }) {
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [form, setForm] = useState({ account_id: '', amount: '', per_usd: '', tx_date: today(), description: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([accountingAPI.accounts(), accountingAPI.currencies()]).then(([a, c]) => {
      setAccounts(a.data); setCurrencies(c.data);
      if (a.data.length) pick(a.data[0], c.data);
    }).catch(() => toast.error(t('error')));
  }, []); // eslint-disable-line

  const acc = accounts.find(a => a.id === form.account_id);
  const pick = (a, cs = currencies) => {
    const rate = cs.find(c => c.code === a.currency_code)?.per_usd || '';
    setForm(f => ({ ...f, account_id: a.id, per_usd: rate, amount: suggestedUsd && rate ? +(suggestedUsd * rate).toFixed(a.currency_code === 'USD' ? 2 : 0) : f.amount }));
  };
  const usd = useMemo(() => {
    const a = parseFloat(form.amount), r = parseFloat(form.per_usd);
    return a > 0 && r > 0 ? a / r : null;
  }, [form.amount, form.per_usd]);

  const submit = async () => {
    if (!form.account_id) return toast.error(t('select_account'));
    if (!(parseFloat(form.amount) > 0)) return toast.error(t('invalid_quantity'));
    if (!(parseFloat(form.per_usd) > 0)) return toast.error(t('rate_missing'));
    setSaving(true);
    try {
      await onSubmit({ account_id: form.account_id, amount: parseFloat(form.amount), per_usd: parseFloat(form.per_usd), tx_date: form.tx_date, description: form.description || undefined });
      toast.success(t('saved')); onClose(true);
    } catch (e) { toast.error(apiError(e, t)); } finally { setSaving(false); }
  };

  return (
    <Modal title={title} onClose={() => onClose(false)} style={{ maxWidth: 480 }}
      footer={<><button className="btn btn-secondary" onClick={() => onClose(false)}>{t('cancel')}</button>
        <button className="btn btn-primary" onClick={submit} disabled={saving}>{saving ? t('loading') : (submitLabel || t('save'))}</button></>}>
      {suggestedUsd != null && <div className="alert alert-warning mb-3"><span>{t('open_amount')}: <strong>{money(suggestedUsd)}</strong></span></div>}
      {accounts.length === 0 ? <div className="alert alert-error"><span>{t('no_accounts_hint')}</span></div> : (
        <>
          <div className="form-group"><label className="form-label">{t('account')} *</label>
            <select className="select" value={form.account_id} onChange={e => pick(accounts.find(a => a.id === e.target.value))}>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.name} ({a.currency_code})</option>)}
            </select>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">{t('amount')} ({acc?.currency_code}) *</label>
              <input className="input" type="number" min="0" step="any" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} /></div>
            <div className="form-group"><label className="form-label">{t('rate_per_usd')}</label>
              <input className="input" type="number" min="0" step="any" disabled={acc?.currency_code === 'USD'} value={form.per_usd} onChange={e => setForm(f => ({ ...f, per_usd: e.target.value }))} /></div>
          </div>
          {usd != null && acc?.currency_code !== 'USD' && <p className="text-xs mb-3" style={{ color: 'var(--green)' }}>≈ {money(usd)}</p>}
          <div className="form-row">
            <div className="form-group"><label className="form-label">{t('date')}</label><input className="input" type="date" value={form.tx_date} onChange={e => setForm(f => ({ ...f, tx_date: e.target.value }))} /></div>
            <div className="form-group"><label className="form-label">{t('description')}</label><input className="input" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} /></div>
          </div>
          {extra}
        </>
      )}
      {acc && <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{t('balance')}: {fmt(acc.balance)} {acc.currency_code}</p>}
    </Modal>
  );
}
